import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, useFetch, fmtErr, fmtDate } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat } from "@/components/shieldx/Primitives";
import { DemoTag } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Crosshair, ShieldCheck, Play, AlertTriangle, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";

const ttStyle = { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, fontFamily: "JetBrains Mono" };
const RUN_STEPS = ["Authorizing simulation scope", "Generating synthetic autonomous identities", "Executing controlled scenarios", "Evaluating protection controls", "Recording evidence & findings", "Computing defense rating"];

function ratingTone(level) {
  if (level === "EXCELLENT") return "text-emerald-400";
  if (level === "STRONG") return "text-blue-400";
  if (level === "NEEDS IMPROVEMENT") return "text-amber-400";
  if (level === "HIGH RISK") return "text-orange-400";
  return "text-red-400";
}

export default function Simulation() {
  const navigate = useNavigate();
  const { data: assetsData, loading: la } = useFetch("/simulation/eligible-assets");
  const { data: scen } = useFetch("/simulation/scenarios");
  const { data: history, loading: lh, reload: reloadHistory } = useFetch("/simulations");
  const { data: trend, reload: reloadTrend } = useFetch("/simulations/trend");

  const [assetId, setAssetId] = useState("");
  const [selected, setSelected] = useState([]);
  const [environment, setEnvironment] = useState("Production");
  const [authorized, setAuthorized] = useState(false);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (scen?.scenarios && selected.length === 0) setSelected(scen.scenarios.map((s) => s.key));
  }, [scen]); // eslint-disable-line

  useEffect(() => {
    if (!assetId && assetsData?.assets) {
      const first = assetsData.assets.find((a) => a.eligible);
      if (first) setAssetId(first.id);
    }
  }, [assetsData]); // eslint-disable-line

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, RUN_STEPS.length - 1)), 420);
    return () => clearInterval(t);
  }, [running]);

  if (la) return <Loader label="Loading attack simulation" />;
  const assets = assetsData?.assets || [];
  const scenarios = scen?.scenarios || [];
  const selectedAsset = assets.find((a) => a.id === assetId);
  const canRun = selectedAsset?.eligible && authorized && selected.length > 0 && !running;

  const toggle = (k) => setSelected((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const run = async () => {
    setRunning(true); setStep(0);
    try {
      const sc = await apiPost("/simulations", {
        asset_id: assetId, scenario_keys: selected, environment, authorized: true,
        authorized_by: undefined, duration: "Instant (controlled)",
      });
      setTimeout(() => {
        toast.success(`Simulation ${sc.sim_id} complete — Defense Rating ${sc.overall}/100`);
        reloadHistory(); reloadTrend();
        navigate(`/app/simulation/${sc.sim_id}`);
      }, 1200);
    } catch (e) {
      setRunning(false);
      toast.error(fmtErr(e?.response?.data?.detail) || "Simulation failed");
    }
  };

  return (
    <div>
      <PageHeader icon={Crosshair} title="Attack Simulation"
        subtitle="Controlled, non-destructive autonomous-threat simulation against your verified protected assets. Scores are computed only from simulated results."
        actions={<DemoTag />} />

      {running && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 backdrop-blur-sm" data-testid="sim-running-overlay">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-7">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-blue-400">
              <span className="h-2 w-2 rounded-full bg-blue-500 sx-pulse" /> Controlled Simulation Running
            </div>
            <div className="mt-5 space-y-2.5">
              {RUN_STEPS.map((s, i) => (
                <div key={s} className={`flex items-center gap-2.5 text-sm ${i <= step ? "text-slate-200" : "text-slate-600"}`}>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] ${i < step ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : i === step ? "border-blue-500/40 bg-blue-500/10 text-blue-400" : "border-slate-700 text-slate-600"}`}>
                    {i < step ? "✓" : i + 1}
                  </span>
                  {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Run Controlled Simulation" desc="Authorize → scope → safe scenarios → scorecard" className="lg:col-span-2" testid="sim-run-panel">
          <div className="space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Target Protected Asset</label>
              <select data-testid="sim-asset-select" value={assetId} onChange={(e) => setAssetId(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm text-slate-200 focus:border-blue-500 focus:outline-none">
                {assets.map((a) => (
                  <option key={a.id} value={a.id} disabled={!a.eligible}>
                    {a.name} · {a.type} {a.eligible ? `(${a.sx_id})` : "— verification required"}
                  </option>
                ))}
              </select>
              {selectedAsset && !selectedAsset.eligible && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300" data-testid="sim-ineligible-notice">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Simulation unavailable. {selectedAsset.reason}
                </div>
              )}
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Scenarios ({selected.length} selected)</label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {scenarios.map((s) => (
                  <button key={s.key} type="button" onClick={() => toggle(s.key)} data-testid={`sim-scenario-${s.key}`}
                    className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${selected.includes(s.key) ? "border-blue-500/50 bg-blue-500/5" : "border-slate-800 bg-slate-950/40 hover:border-slate-700"}`}>
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[9px] ${selected.includes(s.key) ? "border-blue-500 bg-blue-600 text-white" : "border-slate-600"}`}>
                      {selected.includes(s.key) ? "✓" : ""}
                    </span>
                    <span>
                      <span className="block text-xs font-semibold text-slate-200">{s.category}</span>
                      <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-wide text-slate-500">{s.severity} · expects {s.expected.replace(/_/g, " ")}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Environment</label>
                <select data-testid="sim-env-select" value={environment} onChange={(e) => setEnvironment(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm text-slate-200 focus:border-blue-500 focus:outline-none">
                  {["Production", "Staging", "Development"].map((e) => <option key={e}>{e}</option>)}
                </select>
              </div>
            </div>

            <label className="flex items-start gap-2.5 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-3 text-xs text-slate-300">
              <input type="checkbox" data-testid="sim-authorize-check" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-blue-600" />
              I confirm I am authorized to run a controlled simulation against this asset. No real system is attacked — scenarios are safe and non-destructive.
            </label>

            <button onClick={run} disabled={!canRun} data-testid="sim-run-button"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40">
              <Play className="h-4 w-4" /> Authorize & Run Simulation
            </button>
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel title="Defense Validation Trend" desc="Controlled simulation trend" testid="sim-trend-panel">
            {trend?.trend?.length ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={trend.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={ttStyle} />
                  <Line type="monotone" dataKey="rating" stroke="#2563EB" strokeWidth={2} dot={{ r: 3, fill: "#2563EB" }} name="Defense Rating" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-10 text-center text-xs text-slate-500">Run a simulation to begin tracking your controlled-simulation trend.</p>
            )}
            <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-widest text-slate-600">Controlled Simulation Trend</p>
          </Panel>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Simulations" value={history?.length || 0} tone="blue" />
            <Stat label="Eligible Assets" value={assets.filter((a) => a.eligible).length} tone="emerald" sub={`of ${assets.length} total`} />
          </div>
        </div>
      </div>

      <Panel title="Security Rating History" desc="Past controlled simulations and their defense ratings" className="mt-4" testid="sim-history-panel">
        {lh ? <Loader label="Loading history" /> : (
          <Table testid="sim-history-table" onRowClick={(r) => navigate(`/app/simulation/${r.sim_id}`)}
            empty="No simulations yet. Run your first controlled simulation above."
            columns={[
              { key: "date", label: "Date", render: (r) => <span className="font-mono text-xs text-slate-400">{fmtDate(r.created_at)}</span> },
              { key: "asset", label: "Asset", render: (r) => <span className="text-slate-200">{r.asset_name}</span> },
              { key: "sim_id", label: "Simulation", render: (r) => <span className="font-mono text-xs text-slate-400">{r.sim_id}</span> },
              { key: "rating", label: "Rating", render: (r) => <span className={`font-display font-bold ${ratingTone(r.level)}`}>{r.overall}<span className="text-xs text-slate-600">/100</span></span> },
              { key: "level", label: "Status", render: (r) => <span className={`font-mono text-[11px] ${ratingTone(r.level)}`}>{r.level}</span> },
              { key: "scenarios", label: "Scenarios", render: (r) => <span className="text-slate-400">{r.total_scenarios}</span> },
              { key: "findings", label: "Findings", render: (r) => r.findings_count > 0 ? <span className="inline-flex items-center gap-1 text-amber-400"><AlertTriangle className="h-3 w-3" />{r.findings_count}</span> : <span className="text-emerald-400">0</span> },
              { key: "go", label: "", render: () => <ChevronRight className="h-4 w-4 text-slate-600" /> },
            ]}
            rows={history || []} />
        )}
      </Panel>
    </div>
  );
}
